import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { colors } from '../config/colors';
import { sendOtp, verifyOtp, selectAuthLoading, selectAuthError } from '../store/slices/authSlice';
import { fetchCVODashboard } from '../store/slices/dashboardSlice';
import { dashboardService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import ValidationFlow from './ValidationFlow';
import Captcha, { validateCaptcha } from './Captcha';

const CVOLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { login, isAuthenticated, getUserRole, getDashboardPath } = useAuth();
  const authLoading = useSelector(selectAuthLoading);
  const authError = useSelector(selectAuthError);

  const [step, setStep] = useState('email'); // 'email', 'otp', 'validation'
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpToken, setOtpToken] = useState(null);
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaKey, setCaptchaKey] = useState('');
  const [captchaInputOtp, setCaptchaInputOtp] = useState('');
  const [captchaKeyOtp, setCaptchaKeyOtp] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [refreshTriggerOtp, setRefreshTriggerOtp] = useState(0);

  // Check if already authenticated on component mount
  useEffect(() => {
    if (isAuthenticated) {
      // User is already logged in, redirect to their appropriate dashboard
      const dashboardPath = getDashboardPath();
      navigate(dashboardPath, { replace: true });
    }
  }, [isAuthenticated, getDashboardPath, navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (isLoading) return;

    const newErrors = {};

    if (loginMethod === 'email') {
      if (!email) {
        newErrors.email = 'Email is required';
      } else if (!validateEmail(email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else {
      if (!phone) {
        newErrors.phone = 'Phone number is required';
      } else if (!validatePhone(phone)) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
    }

    // Validate CAPTCHA (frontend basic check)
    const captchaValidation = validateCaptcha(captchaInput);
    if (!captchaValidation.isValid) {
      newErrors.captcha = captchaValidation.error;
    }

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);
      try {
        // Send OTP using Redux dispatch
        const result = await dispatch(sendOtp({
          login: loginMethod === 'email' ? email : phone,
          type: 'cvo',
          captcha: captchaInput,
          captcha_key: captchaKey
        }));

        // 🔐 SECURITY: Check for otp_token in response, not just fulfilled status
        // This prevents Burp Suite from faking 200 OK without the actual token
        if (sendOtp.fulfilled.match(result) && result.payload?.otp_token) {
          setStep('otp');
          setErrors({});
          setOtpToken(result.payload.otp_token); // Store the OTP token
          // Reset CAPTCHA after successful OTP send
          setCaptchaInput('');
          setRefreshTrigger(prev => prev + 1);
          setCaptchaInputOtp('');
          setRefreshTriggerOtp(prev => prev + 1);
        } else if (sendOtp.fulfilled.match(result) && !result.payload?.otp_token) {
          // Security error: Got success but no token (possible manipulation)
          const fieldName = loginMethod === 'email' ? 'email' : 'phone';
          setErrors({ [fieldName]: 'Security Error: Invalid server response. Please try again.' });
          setRefreshTrigger(prev => prev + 1);
          setCaptchaInput('');
        } else {
          // Handle error - show it in the appropriate field
          const fieldName = loginMethod === 'email' ? 'email' : 'phone';
          const errorMsg = result.payload || 'Failed to send OTP';

          setErrors({ [fieldName]: errorMsg });
          setRefreshTrigger(prev => prev + 1);
          setCaptchaInput('');
        }
      } catch (error) {
        setErrors({ general: 'An unexpected error occurred' });
        setRefreshTrigger(prev => prev + 1);
        setCaptchaInput('');
      } finally {
        setTimeout(() => setIsLoading(false), 500);
      }
    } else {
      setErrors(newErrors);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!otp) {
      newErrors.otp = 'OTP is required';
    } else if (otp.length !== 6) {
      newErrors.otp = 'OTP must be 6 digits';
    }

    // Validate CAPTCHA (frontend basic check)
    const captchaValidation = validateCaptcha(captchaInputOtp);
    if (!captchaValidation.isValid) {
      newErrors.captchaOtp = captchaValidation.error;
    }

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);
      setErrors({});

      try {
        // Verify OTP using Redux dispatch
        const result = await dispatch(verifyOtp({
          login: loginMethod === 'email' ? email : phone,
          otp: otp,
          type: 'cvo',
          otp_token: otpToken, // Send the stored OTP token
          captcha: captchaInputOtp,
          captcha_key: captchaKeyOtp
        }));

        if (verifyOtp.fulfilled.match(result)) {
          // Update auth context
          login(result.payload.user, result.payload.token);

          // Fetch dashboard data (only for CVO users) and store in Redux
          const userRole = getUserRole();
          if (userRole === 'cvo') {
            // Dispatch to Redux store for useDynamicMenu hook
            dispatch(fetchCVODashboard());
          }

          // Navigate to appropriate dashboard based on user role
          const dashboardPath = getDashboardPath();
          navigate(dashboardPath);
        } else {
          setStep('email'); // Reset flow
          setOtp('');

          // Refresh verification step CAPTCHA on error
          setRefreshTriggerOtp(prev => prev + 1);
          setCaptchaInputOtp('');
          // Show error message
          setErrors({ otp: result.payload || 'Invalid OTP. Please try again.' });
        }
      } catch (error) {
        setStep('email');
        setOtp('');
        setRefreshTriggerOtp(prev => prev + 1);
        setCaptchaInputOtp('');
        setErrors({ otp: 'An error occurred. Please try again.' });
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrors(newErrors);
    }
  };

  const handleResendOtp = async () => {
    if (isLoading) return;

    // Validate CAPTCHA (frontend basic check)
    const captchaValidation = validateCaptcha(captchaInputOtp);
    if (!captchaValidation.isValid) {
      setErrors({ captchaOtp: captchaValidation.error });
      return;
    }

    setIsLoading(true);
    try {
      // Resend OTP using Redux dispatch
      const result = await dispatch(sendOtp({
        login: loginMethod === 'email' ? email : phone,
        type: 'cvo',
        captcha: captchaInputOtp,
        captcha_key: captchaKeyOtp
      }));

      // 🔐 SECURITY: Check for otp_token in response, not just fulfilled status
      if (sendOtp.fulfilled.match(result) && result.payload?.otp_token) {
        setOtp('');
        setErrors({});
        setOtpToken(result.payload.otp_token); // Update the OTP token on resend
        // Reset CAPTCHA after successful OTP send
        setCaptchaInputOtp('');
        setRefreshTriggerOtp(prev => prev + 1);
        // Optionally show success message
      } else if (sendOtp.fulfilled.match(result) && !result.payload?.otp_token) {
        // Security error
        setErrors({ otp: 'Security Error: Invalid server response. Please try again.' });
        setRefreshTriggerOtp(prev => prev + 1);
        setCaptchaInputOtp('');
      } else {
        // Refresh verification step CAPTCHA on error
        setRefreshTriggerOtp(prev => prev + 1);
        setCaptchaInputOtp('');
        setErrors({ otp: result.payload || 'Failed to resend OTP' });
      }
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setOtp('');
    setCaptchaInput('');
    setRefreshTrigger(prev => prev + 1);
    setErrors({});
  };

  // Validation flow handlers
  const handleStartValidation = () => {
    setStep('validation');
    setErrors({});
  };

  const handleValidationComplete = () => {
    // Redirect to login with success message
    setSuccessMessage('Verification successful! Please login to continue.');
    setTimeout(() => {
      setStep('email');
    }, 2000);
  };

  const handleBackToLogin = () => {
    setStep('email');
    setCaptchaInput('');
    setRefreshTrigger(prev => prev + 1);
    setErrors({});
    setSuccessMessage('');
  };

  // If validation step is active, render the centralized ValidationFlow component
  if (step === 'validation') {
    return (
      <ValidationFlow
        validationType="cvo"
        title="CVO Validation"
        subtitle="Verify your identity to access the CVO portal"
        onValidationComplete={handleValidationComplete}
        onBack={handleBackToLogin}
        backPath="/cvo-login"
        showBackToHome={false}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt="SJVN Limited Logo"
            className="w-18 h-24 mx-auto mb-4"
            role="img"
            aria-label="SJVN Limited Logo"
          />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">CVO Login</h2>
          <p className="text-gray-600">Chief Vigilance Officer Portal</p>
          <div className="w-24 h-1 bg-primary-600 mx-auto mt-4"></div>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Success Message */}
          {successMessage && step === 'email' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-green-800 font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-6" role="form" aria-label="CVO Login Form" autoComplete="off">
              {/* Login Method Selection */}
              <div className="space-y-4">
                <label className="text-sm font-medium text-gray-700" htmlFor="login-method">
                  Choose Login Method
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMethod('email');
                      setRefreshTrigger(prev => prev + 1);
                      setCaptchaInput('');
                    }}
                    className={`p-3 rounded-lg border-2 transition-all duration-300 ${loginMethod === 'email'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    aria-pressed={loginMethod === 'email'}
                    aria-label="Login with email"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium">Email</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMethod('phone');
                      setRefreshTrigger(prev => prev + 1);
                      setCaptchaInput('');
                    }}
                    className={`p-3 rounded-lg border-2 transition-all duration-300 ${loginMethod === 'phone'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    aria-pressed={loginMethod === 'phone'}
                    aria-label="Login with phone number"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="font-medium">Phone</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Email Input */}
              {loginMethod === 'email' && (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="off"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your email address"
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    aria-invalid={errors.email ? 'true' : 'false'}
                  />
                  {errors.email && (
                    <p id="email-error" className="mt-2 text-sm text-red-600" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>
              )}

              {/* Phone Input */}
              {loginMethod === 'phone' && (
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="off"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter your 10-digit phone number"
                    maxLength="10"
                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                  />
                  {errors.phone && (
                    <p id="phone-error" className="mt-2 text-sm text-red-600" role="alert">
                      {errors.phone}
                    </p>
                  )}
                </div>
              )}

              {/* CAPTCHA */}
              <Captcha
                value={captchaInput}
                onChange={(e) => {
                  setCaptchaInput(e.target.value);
                  if (errors.captcha) {
                    setErrors(prev => ({ ...prev, captcha: '' }));
                  }
                }}
                error={errors.captcha}
                disabled={isLoading}
                onCaptchaChange={(data) => setCaptchaKey(data.key)}
                refreshTrigger={refreshTrigger}
              />

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-md ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:from-primary-700 hover:to-primary-800 hover:shadow-lg'
                  }`}
                aria-label="Send OTP to login"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send OTP'
                )}
              </button>

              {/* Validate Yourself Link - Inside Login Form Card */}
              <div className="text-center pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">New user? Need to verify your account?</p>
                <button
                  type="button"
                  onClick={handleStartValidation}
                  className="text-primary-600 hover:text-primary-700 font-semibold transition-colors duration-300 inline-flex items-center space-x-2"
                  aria-label="Validate your email and phone"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>Validate Yourself</span>
                </button>
              </div>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleOtpSubmit} className="space-y-6" role="form" aria-label="OTP Verification Form" autoComplete="off">
              {/* OTP Input */}
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  OTP send Successfully
                </p>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  autoComplete="off"
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`w-full px-4 py-3 border rounded-lg text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-300 ${errors.otp ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="000000"
                  maxLength="6"
                  aria-describedby={errors.otp ? 'otp-error' : undefined}
                  aria-invalid={errors.otp ? 'true' : 'false'}
                />
                {errors.otp && (
                  <p id="otp-error" className="mt-2 text-sm text-red-600" role="alert">
                    {errors.otp}
                  </p>
                )}
              </div>

              {/* CAPTCHA for OTP Verification */}
              <Captcha
                value={captchaInputOtp}
                onChange={(e) => {
                  setCaptchaInputOtp(e.target.value);
                  if (errors.captchaOtp) {
                    setErrors(prev => ({ ...prev, captchaOtp: '' }));
                  }
                }}
                error={errors.captchaOtp}
                disabled={isLoading}
                onCaptchaChange={(data) => setCaptchaKeyOtp(data.key)}
                refreshTrigger={refreshTriggerOtp}
              />

              {/* Action Buttons */}
              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-md hover:shadow-lg ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:from-primary-700 hover:to-primary-800'
                    }`}
                  aria-label="Verify OTP and login"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    'Verify OTP & Login'
                  )}
                </button>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={handleBackToEmail}
                    disabled={isLoading}
                    className={`text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    aria-label="Go back to email/phone input"
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={isLoading}
                    className={`text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    aria-label="Resend OTP"
                  >
                    Resend OTP
                  </button>
                </div>
              </div>
            </form>
          )}
          {/* Back to Home */}
          <div className="text-center">
            <button
              onClick={() => navigate('/vigilance-login')}
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-300"
              aria-label="Go back to login page"
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVOLogin;